import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getUserApi, patchUserStatusApi } from "../../api";

export const IS_RESEARCH_PARTICIPANT_URL_PARAM = "participant";
export const IS_PRE_TEST_SURVEY_COMPLETE_URL_PARAM = "prsr";
export const IS_POST_TEST_SURVEY_COMPLETE_URL_PARAM = "posr";

interface UserStatusData {
  isResearchParticipant: boolean;
  isPreTestSurveyComplete: boolean;
  isPostTestSurveyComplete: boolean;
}

export interface UserStatus extends UserStatusData {
  createdAt: string;
  updatedAt: string;
}

export interface User {
  userId: string;
  status: UserStatus;
  createdAt: string;
  lastLogin: string;
}

const parseBooleanParam = (value: string | null): boolean | undefined => {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
};

const getStatusPatchFromURL = (): Partial<UserStatusData> => {
  const urlParams = new URLSearchParams(window.location.search);

  const isResearchParticipant = parseBooleanParam(
    urlParams.get(IS_RESEARCH_PARTICIPANT_URL_PARAM),
  );
  const isPreTestSurveyComplete = parseBooleanParam(
    urlParams.get(IS_PRE_TEST_SURVEY_COMPLETE_URL_PARAM),
  );
  const isPostTestSurveyComplete = parseBooleanParam(
    urlParams.get(IS_POST_TEST_SURVEY_COMPLETE_URL_PARAM),
  );

  const patch: Partial<UserStatusData> = {};

  if (isResearchParticipant !== undefined) {
    patch.isResearchParticipant = isResearchParticipant;
  }

  if (isPreTestSurveyComplete !== undefined) {
    patch.isPreTestSurveyComplete = isPreTestSurveyComplete;
  }

  if (isPostTestSurveyComplete !== undefined) {
    patch.isPostTestSurveyComplete = isPostTestSurveyComplete;
  }

  return patch;
};

const clearStatusParamsFromURL = () => {
  const url = new URL(window.location.href);

  url.searchParams.delete(IS_RESEARCH_PARTICIPANT_URL_PARAM);
  url.searchParams.delete(IS_PRE_TEST_SURVEY_COMPLETE_URL_PARAM);
  url.searchParams.delete(IS_POST_TEST_SURVEY_COMPLETE_URL_PARAM);

  window.history.replaceState({}, "", url.toString());
};

export const hydrateUser = createAsyncThunk<
  User | null,
  string,
  { rejectValue: string }
>("user/hydrateUser", async (userId, thunkApi) => {
  try {
    const remoteUser = await getUserApi(userId);
    if (!remoteUser) return null;
    const patch = getStatusPatchFromURL();

    if (Object.keys(patch).length === 0) {
      return remoteUser;
    }

    const updatedStatus = await patchUserStatusApi(userId, patch);

    clearStatusParamsFromURL();

    return { ...remoteUser, status: updatedStatus };
  } catch (error) {
    return thunkApi.rejectWithValue(
      error instanceof Error ? error.message : "Failed to hydrate user",
    );
  }
});

interface UserState {
  data: User | null;
  status: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
}

const initialState: UserState = {
  data: null,
  status: "idle",
  error: null,
};

const UserSlice = createSlice({
  name: "user",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(hydrateUser.pending, (state) => {
        state.status = "pending";
        state.error = null;
      })
      .addCase(hydrateUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.data = action.payload;
      })
      .addCase(hydrateUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? null;
        state.error =
          action.payload ?? action.error.message ?? "Failed to hydrate user";
      });
  },
});

export default UserSlice.reducer;
